import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { docCode, manualService, useNav, type PageView } from "./api";
import styles from "./manual.module.css";
import { hasReadablePage, pagePlace } from "./navTree";
import { sanitizeManualHtml } from "./sanitize";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ChevronLeftIcon, ChevronRightIcon, PencilIcon } from "@/components/icons";
import { PageHeader } from "@/components/PageHeader";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ApiError } from "@/types/api";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface OutlineItem {
  id: string;
  text: string;
  level: 2 | 3;
}

function slugify(text: string, seen: Map<string, number>): string {
  const base =
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section";
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  return count === 0 ? base : `${base}-${count}`;
}

// The client's own unfinished-content markers, kept word for word. A paragraph
// that opens with one is a gap to fill; the inline ones are links still to add.
// In a list item or table cell a callout would break the list or the cell, so
// there the [PLACEHOLDER…] marker gets a pill like the inline ones.
const PLACEHOLDER_START = "[PLACEHOLDER";
const INLINE_MARKER = /\[Link to [^\]]*\]|\[See: [^\]]*\]|\[ADD LINK[^\]]*\]|\[TBC\]|\$\[Insert\]/g;
const NESTED_MARKER = new RegExp(`\\[PLACEHOLDER[^\\]]*\\]|${INLINE_MARKER.source}`, "g");
const NESTED = "li, td, th";

/** Mark the client's unfinished content in place: each top-level
 * `[PLACEHOLDER…` paragraph goes in a callout under a "To be completed" label,
 * and each inline marker in a pill, never inside a link or code. Text nodes are
 * split and wrapped, never rewritten, and an element already marked is skipped,
 * so a re-run changes nothing. */
function markUnfinished(root: HTMLElement): void {
  root.querySelectorAll("p").forEach((p) => {
    if (!p.textContent?.trimStart().startsWith(PLACEHOLDER_START)) return;
    if (p.closest(NESTED)) return;
    if (p.parentElement?.classList.contains(styles.placeholder ?? "")) return;
    const box = document.createElement("div");
    box.className = styles.placeholder ?? "";
    const label = document.createElement("div");
    label.className = styles.placeholderLabel ?? "";
    label.textContent = "To be completed";
    p.replaceWith(box);
    box.append(label, p);
  });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) texts.push(n as Text);
  for (const text of texts) {
    const parent = text.parentElement;
    if (!parent || parent.classList.contains(styles.marker ?? "")) continue;
    if (parent.closest("a, code")) continue;
    const value = text.data;
    const matches = [...value.matchAll(parent.closest(NESTED) ? NESTED_MARKER : INLINE_MARKER)];
    if (matches.length === 0) continue;
    const frag = document.createDocumentFragment();
    let at = 0;
    for (const m of matches) {
      const start = m.index ?? 0;
      if (start > at) frag.append(value.slice(at, start));
      const pill = document.createElement("span");
      pill.className = styles.marker ?? "";
      pill.textContent = m[0];
      frag.append(pill);
      at = start + m[0].length;
    }
    if (at < value.length) frag.append(value.slice(at));
    text.replaceWith(frag);
  }
}

// A leading doc number such as "4.6", "2.1." or "7.3." on the client's headings.
const LEAD_NUMBER = /^\s*(\d+(?:\.\d+)*)\.?\s+/;

function sameWords(a: string, b: string): boolean {
  const words = (t: string) =>
    t
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim();
  return words(a) === words(b);
}

// A part label such as "PART B — In Operation" or "PART 12: Setup".
const PART_LABEL = /^\s*PART\s+(?:[A-Z]|\d+)\b/;

/** Most seeded pages open with an H2 that repeats the page title after its doc
 * number. Hide that H2 (it stays in the HTML, so a re-run finds it again) and
 * return its number for the meta row. When the first H2 is a part label, the
 * label stays and the H2 right after it is the one checked. Returns undefined
 * when there is no such heading; null when there is but it has no number. */
function hideTitleHeading(root: HTMLElement, title: string): string | null | undefined {
  let first = root.firstElementChild;
  if (
    first instanceof HTMLElement &&
    first.tagName === "H2" &&
    PART_LABEL.test(first.textContent ?? "")
  ) {
    first = first.nextElementSibling;
  }
  if (!(first instanceof HTMLElement) || first.tagName !== "H2") return undefined;
  const text = first.textContent ?? "";
  const number = LEAD_NUMBER.exec(text)?.[1] ?? null;
  if (!sameWords(text.replace(LEAD_NUMBER, ""), title)) return undefined;
  first.hidden = true;
  return number;
}

/** A published manual page: title block, article body, sources, attachments. */
export function ManualPage() {
  const { sectionSlug = "", pageSlug = "" } = useParams();
  const isHowWeAreOrganised =
    sectionSlug === "company-overview" && pageSlug === "how-we-are-organised";
  const isAccentRailPage = sectionSlug === "hr" && pageSlug === "code-of-conduct";
  const location = useLocation();
  const { permissions } = useCurrentUser();
  const { sections } = useNav();
  const [page, setPage] = useState<PageView | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const articleRef = useRef<HTMLDivElement>(null);
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [leadNumber, setLeadNumber] = useState<string | null>(null);

  // Build the "on this page" outline from the rendered article's H2/H3s once
  // the sanitised HTML is in the DOM, tagging each with a stable id to jump to.
  // Also hardens editor-inserted images against layout shift + eager loading
  // (they arrive as bare <img> in the sanitised HTML, so we can't set these in
  // JSX): lazy below the fold, async decode. Each image opens full size in a
  // new tab so a phone reader can zoom a diagram, and each table sits in a
  // horizontal scroll box so a wide one never cuts columns off on a phone.
  // Both wraps skip an element already wrapped, so a re-run changes nothing.
  // The client's unfinished-content markers get their callout and pills here,
  // and a first H2 that only repeats the title is hidden, its number kept.
  useEffect(() => {
    const root = articleRef.current;
    if (!root || !page) {
      setOutline([]);
      setLeadNumber(null);
      return;
    }
    setLeadNumber(hideTitleHeading(root, page.title) ?? null);
    const seen = new Map<string, number>();
    const items: OutlineItem[] = [];
    root.querySelectorAll<HTMLElement>("h2, h3").forEach((heading) => {
      if (heading.hidden) return;
      const text = heading.textContent?.trim() ?? "";
      if (!text) return;
      const id = slugify(text, seen);
      heading.id = id;
      items.push({ id, text, level: heading.tagName === "H3" ? 3 : 2 });
    });
    root.querySelectorAll("img").forEach((img) => {
      img.loading = "lazy";
      img.decoding = "async";
      const src = img.getAttribute("src");
      if (!src || img.closest("a")) return;
      const link = document.createElement("a");
      link.href = src;
      link.target = "_blank";
      link.rel = "noopener";
      link.className = styles.imageLink ?? "";
      img.replaceWith(link);
      link.appendChild(img);
    });
    root.querySelectorAll("table").forEach((table) => {
      if (table.parentElement?.classList.contains(styles.tableScroll ?? "")) return;
      const box = document.createElement("div");
      box.className = styles.tableScroll ?? "";
      table.replaceWith(box);
      box.appendChild(table);
    });
    markUnfinished(root);
    setOutline(items);
  }, [page]);

  // Scrollspy: highlight the heading the reader is currently under, in both
  // the margin rail and the inline outline. A heading is "current" once it
  // reaches the top ~30% of the scroll viewport (rootMargin bottom -70%).
  useEffect(() => {
    const root = articleRef.current;
    if (!root || outline.length === 0) return;
    const headings = outline
      .map((o) => document.getElementById(o.id))
      .filter((el): el is HTMLElement => el !== null);
    if (headings.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.add(e.target.id);
          else visible.delete(e.target.id);
        }
        const current = outline.find((o) => visible.has(o.id));
        if (current) setActiveId(current.id);
      },
      { rootMargin: "0px 0px -70% 0px" }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [outline]);

  // A citation from the chat assistant (or any deep link) lands on this page
  // with a #anchor matching a heading id assigned above — scroll to it once
  // the headings actually exist in the DOM (React Router doesn't do this
  // automatically for client-side navigation).
  useEffect(() => {
    if (!location.hash) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [outline, location.hash]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPage(null);
    manualService
      .getPage(sectionSlug, pageSlug)
      .then((view) => {
        if (cancelled) return;
        setPage(view);
        setStatus("ready");
        // Feed the home page's "continue reading" card. Local-only by design:
        // per-user reading position is exactly what core.usage_log must not
        // hold (it's content-free), so it never leaves this browser.
        try {
          localStorage.setItem(
            "opshub:last-read",
            JSON.stringify({
              path: `/manual/${sectionSlug}/${pageSlug}`,
              title: view.title,
              sectionTitle: view.section.title,
              at: Date.now(),
            })
          );
        } catch {
          /* storage full/blocked — the nicety just doesn't appear */
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err instanceof ApiError && err.status === 404 ? "missing" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [sectionSlug, pageSlug]);

  if (status === "loading") {
    return <div className={`${styles.view} ${styles.quiet}`}>Loading…</div>;
  }
  if (status === "missing" || status === "error") {
    return (
      <div className={styles.view}>
        <div className={styles.emptyState}>
          <strong>Page not found.</strong>
          <br />
          The page doesn&rsquo;t exist, or isn&rsquo;t available to you. If you think you should
          have access, ask your manager.
        </div>
      </div>
    );
  }
  if (!page) return null;

  // A child's code adds its own ordinal after its parent's: OM-04.12.3.
  const code = page.parent
    ? docCode(page.section.ordinal, page.parent.ordinal, page.ordinal)
    : docCode(page.section.ordinal, page.ordinal);
  const parentPath = page.parent ? `/manual/${page.section.slug}/${page.parent.slug}` : null;
  // A parent page lists its published children, in order, after its body.
  const navSection = sections?.find((s) => s.slug === page.section.slug);
  const childPages = (navSection ? pagePlace(navSection.pages, page.id)?.children : [])?.filter(
    (c) => c.published
  );
  // The next populated section after this one, for the end-of-section
  // "Next section →" hand-off (null when this is the last one).
  const nextSection = (() => {
    if (page.next || !sections) return null;
    const idx = sections.findIndex((s) => s.slug === page.section.slug);
    if (idx === -1) return null;
    return sections.slice(idx + 1).find(hasReadablePage) ?? null;
  })();
  // Mirror image, backward: the previous populated section, for the
  // start-of-section "← Previous section" hand-off.
  const prevSection = (() => {
    if (page.prev || !sections) return null;
    const idx = sections.findIndex((s) => s.slug === page.section.slug);
    if (idx === -1) return null;
    return sections.slice(0, idx).reverse().find(hasReadablePage) ?? null;
  })();

  // One outline, rendered twice: the margin rail on wide screens and the
  // inline <details> below it. Both highlight the reader's current heading.
  const renderOutline = () =>
    outline.map((item) => (
      <li key={item.id}>
        <a
          href={`#${item.id}`}
          aria-current={activeId === item.id ? "location" : undefined}
          className={[
            styles.railLink,
            item.level === 3 ? styles.railLinkH3 : "",
            activeId === item.id ? styles.railLinkActive : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {item.text}
        </a>
      </li>
    ));

  return (
    <div className={`${styles.pageWithRail} ${isAccentRailPage ? styles.pageAccentRail : ""}`}>
      <div className={styles.mainCol}>
        {/* Breadcrumbs are wayfinding — every crumb is a NAME, no codes. The
            numbers live in the sidebar (the anchor for the code system) and
            the document code + revision in the title block below. */}
        <Breadcrumbs
          className={styles.breadcrumbs}
          items={[
            { label: "OpsHub", to: "/" },
            { label: page.section.title, to: `/manual/${page.section.slug}` },
            ...(page.parent && parentPath ? [{ label: page.parent.title, to: parentPath }] : []),
            { label: page.title },
          ]}
        />
        {/* Phones get one link back to the parent page or section instead of
            the full trail, which wrapped into a jagged block at 375 px. */}
        <Link to={parentPath ?? `/manual/${page.section.slug}`} className={styles.backLink}>
          <span aria-hidden="true">←</span> {page.parent?.title ?? page.section.title}
        </Link>

        <article>
          <PageHeader title={page.title} />
          {/* Controlled-document signature: the citation code + revision and
              the approval provenance. The editor's own way in ("Edit") sits
              here as a quiet right-aligned link, not a prominent button — the
              row is identical for every role. */}
          <div
            className={`${styles.titleBlock} ${isHowWeAreOrganised ? styles.titleBlockAccent : ""}`}
          >
            <span className={styles.tbCode}>
              {code} · v{page.versionNo}
            </span>
            {leadNumber && <span className={styles.tbNumber}>§ {leadNumber}</span>}
            {/* No approver (a seeded page) means no line: decision d545. */}
            {page.approvedBy && (
              <span className={styles.tbCaption}>
                Approved by {page.approvedBy} · {formatDate(page.publishedAt)}
              </span>
            )}
            {permissions.includes("cms.edit") && (
              <Link to={`/edit/${page.id}`} className={styles.tbEdit}>
                <PencilIcon /> Edit
              </Link>
            )}
          </div>

          {outline.length > 1 && (
            <details className={styles.outlineInline}>
              <summary>On this page</summary>
              <ul className={styles.railList}>{renderOutline()}</ul>
            </details>
          )}

          <div
            ref={articleRef}
            className={styles.article}
            // Sanitised: schema-constrained server render + DOMPurify (sanitize.ts).
            dangerouslySetInnerHTML={{ __html: sanitizeManualHtml(page.html) }}
          />

          {childPages && childPages.length > 0 && (
            <ol className={styles.childList} aria-label={`Pages in ${page.title}`}>
              {childPages.map((c) => (
                <li key={c.id}>
                  <Link to={`/manual/${page.section.slug}/${c.slug}`}>{c.title}</Link>
                  {c.snippet && <p className={styles.childSnippet}>{c.snippet}</p>}
                </li>
              ))}
            </ol>
          )}

          {page.sources.length > 0 && (
            <div className={styles.docLinks}>
              <h3 className={styles.docLinksTitle}>Source documents on SharePoint</h3>
              {page.sources.map((s) => (
                <a
                  key={s.id}
                  className={styles.docLink}
                  href={s.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span aria-hidden="true">📄</span>
                  <span className={styles.docLinkName}>{s.name}</span>
                  <span className={styles.docLinkMeta}>SharePoint ↗</span>
                </a>
              ))}
            </div>
          )}

          {page.attachments.length > 0 && (
            <div className={styles.docLinks}>
              <h3 className={styles.docLinksTitle}>Downloads</h3>
              {page.attachments.map((a) => (
                <a key={a.id} className={styles.docLink} href={`/api/cms/assets/${a.id}`}>
                  <span aria-hidden="true">↓</span>
                  <span className={styles.docLinkName}>{a.filename}</span>
                  <span className={styles.docLinkMeta}>{formatSize(a.sizeBytes)}</span>
                </a>
              ))}
            </div>
          )}
        </article>

        <div className={styles.footerNav}>
          {page.prev ? (
            <Link to={`/manual/${page.prev.sectionSlug}/${page.prev.slug}`}>
              <span className={styles.footerDir}>
                <ChevronLeftIcon className={styles.footerChevron} /> Previous
              </span>
              {page.prev.title}
            </Link>
          ) : prevSection ? (
            // First page of the section — hand the reader back into the
            // previous populated section instead of a dead-end.
            <Link to={`/manual/${prevSection.slug}`}>
              <span className={styles.footerDir}>
                <ChevronLeftIcon className={styles.footerChevron} /> Previous section
              </span>
              {prevSection.title}
            </Link>
          ) : (
            <span />
          )}
          {page.next ? (
            <Link
              to={`/manual/${page.next.sectionSlug}/${page.next.slug}`}
              className={styles.footerNext}
            >
              <span className={styles.footerDir}>
                Next <ChevronRightIcon className={styles.footerChevron} />
              </span>
              {page.next.title}
            </Link>
          ) : nextSection ? (
            // Last page of the section — carry the reader into the next
            // populated section instead of dead-ending.
            <Link to={`/manual/${nextSection.slug}`} className={styles.footerNext}>
              <span className={styles.footerDir}>
                Next section <ChevronRightIcon className={styles.footerChevron} />
              </span>
              {nextSection.title}
            </Link>
          ) : (
            <span />
          )}
        </div>
      </div>

      {outline.length >= 3 && (
        <aside className={styles.rail} aria-label="On this page">
          <div className={styles.railSticky}>
            <div className={styles.railHeading}>On this page</div>
            <ul className={styles.railList}>{renderOutline()}</ul>
          </div>
        </aside>
      )}
    </div>
  );
}
